import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API } from '../utils/constants';

const S = {
  bg: "#0a0a0f", card: "#1a1a26", border: "#2a2a3a",
  text: "#f0f0f8", muted: "#8888a8", accent: "#ff6b35", error: "#ff2d55",
};

function Field({ label, value, onChange, type = "text", placeholder, autoComplete, onKeyDown }) {
  const [show, setShow] = useState(false);
  const isPass = type === "password";
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ color: S.muted, fontSize: 11, letterSpacing: 1, display: "block", marginBottom: 6 }}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={isPass && show ? "text" : type} value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder} autoComplete={autoComplete} onKeyDown={onKeyDown}
          onAnimationStart={e => { if (e.animationName === "fitapp-autofill") setTimeout(() => document.activeElement?.blur(), 50); }}
          style={{ width: "100%", background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: isPass ? "14px 46px 14px 16px" : "14px 16px", color: S.text, fontSize: 16, outline: "none", boxSizing: "border-box" }}
        />
        {isPass && (
          <button type="button" onClick={() => setShow(s => !s)}
            style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: show ? S.accent : S.muted, fontSize: 18, lineHeight: 1, padding: 4, zIndex: 2 }}>
            {show ? "🙈" : "👁"}
          </button>
        )}
      </div>
    </div>
  );
}

function Btn({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ width: "100%", padding: 16, borderRadius: 14, background: disabled ? S.card : "linear-gradient(135deg, #ff6b35, #e55a2b)", border: disabled ? `1px solid ${S.border}` : "none", color: disabled ? S.muted : "#fff", fontSize: 16, fontWeight: 800, cursor: disabled ? "default" : "pointer", marginBottom: 12 }}>
      {children}
    </button>
  );
}

// Detecta ?reset_token= en la URL
function getResetToken() {
  const p = new URLSearchParams(window.location.search);
  return p.get("reset_token") || "";
}

export default function LoginScreen({ onLogin }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset | sent | contact | contactSent
  const [email, setEmail] = useState("");

  const [pass, setPass] = useState("");
  const [remember, setRemember] = useState(false);
  const [pass2, setPass2] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Detectar reset_token en la URL
  useEffect(() => {
    const t = getResetToken();
    if (t) { setResetToken(t); setMode("reset"); }
  }, []);

  // Autofill iOS (Face ID / Keychain) → ocultar teclado
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `@keyframes fitapp-autofill { from {} to {} } input:-webkit-autofill { animation-name: fitapp-autofill; animation-duration: 1ms; }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [contactNombre, setContactNombre] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMsg, setContactMsg] = useState("");

  const reset = (m) => { setMode(m); setError(""); setPass(""); setPass2(""); };

  const doContact = async () => {
    if (!contactMsg.trim()) { setError(t('auth.contact.messageRequired')); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch(API + "/contact", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nombre: contactNombre.trim() || "Anónimo", email: contactEmail.trim() || "sin email", mensaje: contactMsg.trim() }),
      });
      if (r.ok) { setMode("contactSent"); setContactNombre(""); setContactEmail(""); setContactMsg(""); }
      else setError(t('auth.contact.sendError'));
    } catch { setError(t('auth.errors.connectionError')); }
    setLoading(false);
  };

  const doLogin = async () => {
    setLoading(true); setError("");
    try {
      const r = await fetch(API + "/auth/login", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase(), password: pass, remember }),
      });
      const d = await r.json();
      if (d.ok) onLogin({ username: d.username, nombre: d.nombre, handle: d.handle });
      else setError(t('auth.errors.invalidCredentials'));
    } catch { setError(t('auth.errors.connectionError')); }
    setLoading(false);
  };

  const doRegister = async () => {
    setError("");
    if (!email.trim() || !email.includes("@")) { setError(t('auth.errors.invalidEmail')); return; }
    if (pass.length < 6) { setError(t('auth.errors.passwordTooShort')); return; }
    if (pass !== pass2) { setError(t('auth.errors.passwordsMismatch')); return; }
    setLoading(true);
    try {
      const r = await fetch(API + "/auth/register", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ password: pass, email: email.trim().toLowerCase() }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        const lr = await fetch(API + "/auth/login", {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ email: email.trim().toLowerCase(), password: pass }),
        });
        const ld = await lr.json();
        if (ld.ok) onLogin({ username: ld.username, nombre: ld.nombre, handle: ld.handle });
      } else setError(d.detail || t('auth.errors.registerError'));
    } catch { setError(t('auth.errors.connectionError')); }
    setLoading(false);
  };

  const doDemo = async () => {
    setLoading(true); setError("");
    try {
      const r = await fetch(API + "/auth/demo", { method: "POST", credentials: "include" });
      const d = await r.json();
      if (d.ok) onLogin({ username: d.username, nombre: d.nombre });
      else setError(t('auth.errors.demoError'));
    } catch { setError(t('auth.errors.connectionError')); }
    setLoading(false);
  };

  const doForgot = async () => {
    if (!email.trim()) { setError(t('auth.forgot.emailRequired')); return; }
    setLoading(true); setError("");
    try {
      await fetch(API + "/auth/forgot-password", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setMode("sent");
    } catch { setError(t('auth.errors.connectionError')); }
    setLoading(false);
  };

  const doReset = async () => {
    setError("");
    if (pass.length < 6) { setError(t('auth.errors.passwordTooShort')); return; }
    if (pass !== pass2) { setError(t('auth.errors.passwordsMismatch')); return; }
    setLoading(true);
    try {
      const r = await fetch(API + "/auth/reset-password", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ token: resetToken, new_password: pass }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        // Limpiar token de la URL sin recargar
        window.history.replaceState({}, "", window.location.pathname);
        setMode("login");
        setError("");
        setPass(""); setPass2("");
      } else setError(d.detail || t('auth.reset.resetError'));
    } catch { setError(t('auth.errors.connectionError')); }
    setLoading(false);
  };

  const Logo = () => (
    <>
      <div style={{ fontSize: 56, marginBottom: 16 }}>◈</div>
      <h1 style={{ color: S.text, fontSize: 28, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif", margin: "0 0 8px" }}>Fitmemo</h1>
      <p style={{ color: S.muted, fontSize: 13, margin: "0 0 32px" }}>{t('auth.appTagline')}</p>
    </>
  );

  // Pantalla reset desde email
  if (mode === "reset") return (
    <div style={{ background: S.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <Logo />
      <div style={{ width: "100%", maxWidth: 340 }}>
        <h2 style={{ color: S.text, fontSize: 20, fontWeight: 800, margin: "0 0 24px", textAlign: "center" }}>{t('auth.reset.title')}</h2>
        <Field label={t('auth.reset.newPasswordLabel')} value={pass} onChange={setPass} type="password" placeholder={t('auth.passwordMinPlaceholder')} autoComplete="new-password" />
        <Field label={t('auth.passwordRepeatLabel')} value={pass2} onChange={setPass2} type="password" placeholder={t('auth.passwordPlaceholder')} autoComplete="new-password" onKeyDown={e => e.key === "Enter" && doReset()} />
        {error && <p style={{ color: S.error, fontSize: 13, textAlign: "center", margin: "0 0 16px" }}>{error}</p>}
        <Btn onClick={doReset} disabled={loading}>{loading ? t('auth.reset.savingBtn') : t('auth.reset.saveBtn')}</Btn>
      </div>
    </div>
  );

  // Pantalla confirmación email enviado
  if (mode === "sent") return (
    <div style={{ background: S.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <Logo />
      <div style={{ width: "100%", maxWidth: 340, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
        <h2 style={{ color: S.text, fontSize: 20, fontWeight: 800, margin: "0 0 12px" }}>{t('auth.sent.title')}</h2>
        <p style={{ color: S.muted, fontSize: 14, margin: "0 0 32px" }}>{t('auth.sent.subtitle')}</p>
        <button onClick={() => reset("login")} style={{ background: "transparent", border: "none", color: S.accent, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>{t('auth.sent.backToLogin')}</button>
      </div>
    </div>
  );

  // Pantalla recuperar contraseña
  if (mode === "forgot") return (
    <div style={{ background: S.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <Logo />
      <div style={{ width: "100%", maxWidth: 340 }}>
        <h2 style={{ color: S.text, fontSize: 20, fontWeight: 800, margin: "0 0 8px", textAlign: "center" }}>{t('auth.forgot.title')}</h2>
        <p style={{ color: S.muted, fontSize: 13, margin: "0 0 24px", textAlign: "center" }}>{t('auth.forgot.subtitle')}</p>
        <Field label={t('auth.emailLabel')} value={email} onChange={setEmail} type="email" placeholder={t('auth.emailPlaceholder')} autoComplete="email" onKeyDown={e => e.key === "Enter" && doForgot()} />
        {error && <p style={{ color: S.error, fontSize: 13, textAlign: "center", margin: "0 0 16px" }}>{error}</p>}
        <Btn onClick={doForgot} disabled={loading}>{loading ? t('auth.forgot.sendingBtn') : t('auth.forgot.sendBtn')}</Btn>
        <button onClick={() => reset("login")} style={{ width: "100%", background: "transparent", border: "none", color: S.muted, fontSize: 13, cursor: "pointer", padding: 8 }}>{t('auth.forgot.backToLogin')}</button>
      </div>
    </div>
  );

  // Pantalla contacto enviado
  if (mode === "contactSent") return (
    <div style={{ background: S.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <Logo />
      <div style={{ width: "100%", maxWidth: 340, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: S.text, fontSize: 20, fontWeight: 800, margin: "0 0 12px" }}>{t('auth.contactSent.title')}</h2>
        <p style={{ color: S.muted, fontSize: 14, margin: "0 0 32px" }}>{t('auth.contactSent.subtitle')}</p>
        <button onClick={() => reset("login")} style={{ background: "transparent", border: "none", color: S.accent, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>{t('auth.contactSent.backToLogin')}</button>
      </div>
    </div>
  );

  // Pantalla formulario de contacto
  if (mode === "contact") return (
    <div style={{ background: S.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <Logo />
      <div style={{ width: "100%", maxWidth: 340 }}>
        <h2 style={{ color: S.text, fontSize: 20, fontWeight: 800, margin: "0 0 8px", textAlign: "center" }}>{t('auth.contact.title')}</h2>
        <p style={{ color: S.muted, fontSize: 13, margin: "0 0 24px", textAlign: "center" }}>{t('auth.contact.subtitle')}</p>
        <Field label={t('auth.contact.nameLabel')} value={contactNombre} onChange={setContactNombre} placeholder={t('auth.contact.namePlaceholder')} />
        <Field label={t('auth.contact.emailLabel')} value={contactEmail} onChange={setContactEmail} type="email" placeholder={t('auth.emailPlaceholder')} />
        <div style={{ marginBottom: 14 }}>
          <label style={{ color: S.muted, fontSize: 11, letterSpacing: 1, display: "block", marginBottom: 6 }}>{t('auth.contact.messageLabel')}</label>
          <textarea
            value={contactMsg}
            onChange={e => setContactMsg(e.target.value)}
            placeholder={t('auth.contact.messagePlaceholder')}
            rows={5}
            style={{ width: "100%", background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: "14px 16px", color: S.text, fontSize: 15, outline: "none", boxSizing: "border-box", resize: "none", fontFamily: "inherit" }}
          />
        </div>
        {error && <p style={{ color: S.error, fontSize: 13, textAlign: "center", margin: "0 0 16px" }}>{error}</p>}
        <Btn onClick={doContact} disabled={loading}>{loading ? t('auth.contact.sendingBtn') : t('auth.contact.sendBtn')}</Btn>
        <button onClick={() => reset("login")} style={{ width: "100%", background: "transparent", border: "none", color: S.muted, fontSize: 13, cursor: "pointer", padding: 8 }}>{t('auth.contact.backToLogin')}</button>
      </div>
    </div>
  );

  // Login / Registro
  return (
    <div style={{ background: S.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <Logo />

      <div style={{ display: "flex", background: S.card, borderRadius: 12, padding: 4, marginBottom: 28, width: "100%", maxWidth: 340 }}>
        {[["login", t('auth.login')], ["register", t('auth.register')]].map(([m, lbl]) => (
          <button key={m} onClick={() => reset(m)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: mode === m ? "linear-gradient(135deg, #ff6b35, #e55a2b)" : "transparent", color: mode === m ? "#fff" : S.muted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{lbl}</button>
        ))}
      </div>

      <div style={{ width: "100%", maxWidth: 340 }}>
        {mode === "login" ? (
          <>
            <Field label={t('auth.emailLabel')} value={email} onChange={setEmail} type="email" placeholder={t('auth.emailPlaceholder')} autoComplete="email" />
            <Field label={t('auth.passwordLabel')} value={pass} onChange={setPass} type="password" placeholder={t('auth.passwordPlaceholder')} autoComplete="current-password" onKeyDown={e => e.key === "Enter" && doLogin()} />
            <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer" }}>
              <div
                onClick={() => setRemember(r => !r)}
                style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${remember ? S.accent : S.border}`, background: remember ? S.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}
              >
                {remember && <span style={{ color: "#fff", fontSize: 13, fontWeight: 900, lineHeight: 1 }}>✓</span>}
              </div>
              <span onClick={() => setRemember(r => !r)} style={{ color: S.muted, fontSize: 13 }}>Permanecer conectado</span>
            </label>
            {error && <p style={{ color: S.error, fontSize: 13, textAlign: "center", margin: "0 0 16px" }}>{error}</p>}
            <Btn onClick={doLogin} disabled={loading}>{loading ? t('auth.loginLoading') : t('auth.loginBtn')}</Btn>
            <button onClick={() => reset("forgot")} style={{ width: "100%", background: "transparent", border: "none", color: S.muted, fontSize: 13, cursor: "pointer", padding: 8 }}>{t('auth.forgotPassword')}</button>
          </>
        ) : (
          <>
            <Field label={t('auth.emailLabel')} value={email} onChange={setEmail} type="email" placeholder={t('auth.emailPlaceholder')} autoComplete="email" />
            <Field label={t('auth.passwordLabel')} value={pass} onChange={setPass} type="password" placeholder={t('auth.passwordMinPlaceholder')} autoComplete="new-password" />
            <Field label={t('auth.passwordRepeatLabel')} value={pass2} onChange={setPass2} type="password" placeholder={t('auth.passwordPlaceholder')} autoComplete="new-password" onKeyDown={e => e.key === "Enter" && doRegister()} />
            {error && <p style={{ color: S.error, fontSize: 13, textAlign: "center", margin: "0 0 16px" }}>{error}</p>}
            <Btn onClick={doRegister} disabled={loading}>{loading ? t('auth.registerLoading') : t('auth.registerBtn')}</Btn>
          </>
        )}
      </div>

      <div style={{ width: "100%", maxWidth: 340, marginTop: 16, textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, height: 1, background: S.border }} />
          <span style={{ color: S.muted, fontSize: 11 }}>O</span>
          <div style={{ flex: 1, height: 1, background: S.border }} />
        </div>
        <button onClick={doDemo} disabled={loading} style={{ width: "100%", padding: "14px 0", borderRadius: 14, background: "rgba(57,255,20,0.12)", border: "1px solid rgba(57,255,20,0.4)", color: "#39FF14", fontSize: 14, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>
          {loading ? "..." : t('auth.demoBtn')}
        </button>
        <button onClick={() => reset("contact")} style={{ background: "transparent", border: "none", color: S.muted, fontSize: 12, cursor: "pointer", marginTop: 16, opacity: 0.7 }}>{t('auth.contact')}</button>

        {/* Banner canal Telegram */}
        <a href="https://t.me/fitmemo_app" target="_blank" rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 20, padding: "12px 16px",
            borderRadius: 14, background: "rgba(0,218,243,0.1)", border: "1px solid rgba(0,218,243,0.3)",
            textDecoration: "none", cursor: "pointer" }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>📣</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#00daf3", fontSize: 13, fontWeight: 700 }}>{t('auth.telegram.follow')}</div>
            <div style={{ color: S.muted, fontSize: 11, marginTop: 2 }}>{t('auth.telegram.subtitle')}</div>
          </div>
          <span style={{ color: "#00daf3", fontSize: 16, flexShrink: 0 }}>→</span>
        </a>
      </div>
    </div>
  );
}
